import React, { useState } from 'react';
import { formatDublinDate, formatDublinTime } from '../../utils/date';
import {
  useListStudentPoliciesQuery,
  useLazyGetPolicyVersionFileQuery,
} from '../../store/api/localApi';
import { PolicyStatus, StudentPolicyAcceptance } from '../../types';

const PolicyPdfViewer = React.lazy(() => import('../../components/policies/PolicyPdfViewer'));

interface Props {
  studentId: string;
}

const badgeFor = (acceptance: StudentPolicyAcceptance): { label: string; className: string } => {
  if (acceptance.policyStatus === PolicyStatus.ARCHIVED) {
    return { label: 'Archived', className: 'bg-amber-100 text-amber-800' };
  }
  return acceptance.isCurrent
    ? { label: 'Current', className: 'bg-green-100 text-green-800' }
    : { label: 'Superseded', className: 'bg-gray-100 text-gray-700' };
};

const lineFor = (acceptance: StudentPolicyAcceptance): string => {
  const when = `${formatDublinDate(acceptance.acknowledgedAt)} at ${formatDublinTime(
    acceptance.acknowledgedAt
  )}`;
  return acceptance.ackType === 'SIGN'
    ? `Version ${acceptance.versionNumber} · Signed by ${acceptance.acknowledgedByName} as "${acceptance.signedName}" on ${when}`
    : `Version ${acceptance.versionNumber} · Acknowledged by ${acceptance.acknowledgedByName} on ${when}`;
};

/**
 * StudentDetail "Policies accepted" section. Every policy acceptance recorded
 * for THIS child, newest first, with a link to open the exact document
 * version that was accepted — the thing a Tusla inspector asks for.
 *
 * Unlike IntakeFormsSection, this always renders (including the empty
 * state): the absence of any accepted policy is itself something an
 * inspector needs to see, not something to hide.
 */
export const StudentPoliciesSection: React.FC<Props> = ({ studentId }) => {
  const { data, isLoading, isError } = useListStudentPoliciesQuery({ studentId });
  const [fetchFile, fileResult] = useLazyGetPolicyVersionFileQuery();
  const [openAckId, setOpenAckId] = useState<string | null>(null);

  const toggleView = async (acceptance: StudentPolicyAcceptance) => {
    if (openAckId === acceptance.ackId) {
      setOpenAckId(null);
      return;
    }
    setOpenAckId(acceptance.ackId);
    await fetchFile({ policyId: acceptance.policyId, versionId: acceptance.versionId });
  };

  if (isLoading) {
    return (
      <section data-testid="student-policies-section" className="mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Policies accepted</h3>
        <p className="text-xs text-gray-500">Loading…</p>
      </section>
    );
  }

  const acceptances = data?.acceptances ?? [];

  return (
    <section data-testid="student-policies-section" className="mt-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Policies accepted</h3>

      {isError ? (
        <p data-testid="student-policies-error" className="text-xs text-red-600">
          Couldn&apos;t load policy acceptances.
        </p>
      ) : acceptances.length === 0 ? (
        <p data-testid="student-policies-empty" className="text-xs text-gray-500">
          No policies accepted yet.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded">
          {acceptances.map((acceptance) => {
            const badge = badgeFor(acceptance);
            const isOpen = openAckId === acceptance.ackId;
            return (
              <li key={acceptance.ackId} data-testid={`student-policy-row-${acceptance.ackId}`}>
                <div className="flex items-center justify-between px-3 py-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">{acceptance.policyTitle}</span>
                      <span
                        data-testid={`student-policy-status-${acceptance.ackId}`}
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{lineFor(acceptance)}</div>
                  </div>
                  <button
                    type="button"
                    data-testid={`student-policy-view-${acceptance.ackId}`}
                    onClick={() => toggleView(acceptance)}
                    className="print:hidden text-xs text-primary-700 underline flex-shrink-0"
                  >
                    View policy
                  </button>
                </div>

                {isOpen &&
                  (() => {
                    // `currentData`, not `data`: RTK keeps the PREVIOUS row's
                    // result in `data` while a new row's fetch is in flight,
                    // so opening row A then row B would show A's document
                    // under B until B's fetch lands (forever if B fails).
                    // `originalArgs` pins the in-flight/loaded result to THIS
                    // row so a stale document is never rendered under the
                    // wrong acceptance. Same reasoning as
                    // AttendancePage.tsx's `currentData` usage.
                    const isThisRow =
                      fileResult.originalArgs?.versionId === acceptance.versionId &&
                      fileResult.originalArgs?.policyId === acceptance.policyId;

                    if (isThisRow && fileResult.currentData?.url) {
                      return (
                        <div className="px-3 pb-3 print:hidden">
                          <React.Suspense
                            fallback={
                              <div
                                data-testid="policy-pdf-loading"
                                className="py-10 text-center text-sm text-gray-500"
                              >
                                Loading the document viewer…
                              </div>
                            }
                          >
                            <PolicyPdfViewer url={fileResult.currentData.url} />
                          </React.Suspense>
                        </div>
                      );
                    }

                    if (fileResult.isFetching) {
                      return (
                        <p
                          data-testid={`student-policy-file-loading-${acceptance.ackId}`}
                          className="px-3 pb-3 text-xs text-gray-500 print:hidden"
                        >
                          Loading the document…
                        </p>
                      );
                    }

                    if (isThisRow && fileResult.isError) {
                      return (
                        <p
                          data-testid={`student-policy-file-error-${acceptance.ackId}`}
                          className="px-3 pb-3 text-xs text-red-600 print:hidden"
                        >
                          Couldn&apos;t open the document.
                        </p>
                      );
                    }

                    return null;
                  })()}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
